import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import { userSchema } from "./useUser";
import { useAuth } from "@clerk/clerk-react";

export const invitationStatusSchema = z.enum(["PENDING", "ACCEPTED", "TIMED_OUT", "DECLINED", "CANCELED"]);

export const invitationSchema = z.object({
  id: z.string(),
  status: invitationStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  parentUserId: z.string(),
  fromEmail: z.string(),
  invitedEmail: z.string(),
  invitationCode: z.string(),
  shortCode: z.string().optional().nullable(),
  clerkInvitationUrl: z.string().optional().nullable(),
  expiryDate: z.string(),
  invitedName: z.string().optional().nullable(),
  relationship: z.string().optional().nullable(),
  techComfort: z.string().optional().nullable(),
  focusAreas: z.string().optional().nullable(),
  personalMessage: z.string().optional().nullable(),
});

export type Invitation = z.infer<typeof invitationSchema>;
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

// Lightweight shape returned by /app/invitations/pending/mine. Intentionally a
// subset of the full invitation row — the catchall redirect only needs enough
// to identify and present the pending invite(s) to the user.
export const myPendingInvitationSchema = z.object({
  invitationCode: z.string(),
  fromEmail: z.string(),
  invitedName: z.string().nullable().optional(),
  expiryDate: z.string(),
  parentUserName: z.string().nullable().optional(),
});

export type MyPendingInvitation = z.infer<typeof myPendingInvitationSchema>;

export const useMyPendingInvitations = (options?: { enabled?: boolean }) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["invitations", "pending", "mine"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/invitations/pending/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return z.array(myPendingInvitationSchema).parse(response.data?.invitations ?? []);
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    retry: false,
  });
};

export const getInvitationByCode = (invitationCode: string) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["invitation", invitationCode],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/invitations/${invitationCode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return invitationSchema.parse(response.data);
    },
    enabled: !!invitationCode,
  });
};

export const acceptInvitation = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const token = await getToken();
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/app/invitations/${invitationCode}/accept`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const updatedInvitation = invitationSchema.parse(response.data.updatedInvitation);
      const updatedUser = userSchema.parse(response.data.updatedUser);
      return { invitation: updatedInvitation, user: updatedUser };
    },
    onMutate: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onSuccess: () => {
      import('@/lib/posthog').then(({ trackEvent }) => {
        trackEvent('invitation_accepted');
      });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};

export const declineInvitation = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const token = await getToken();
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/app/invitations/${invitationCode}/decline`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return invitationSchema.parse(response.data);
    },
    onMutate: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};

export const resendInvitation = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const token = await getToken();
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/app/invitations/${invitationCode}/resend`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return invitationSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family"] });
    },
  });
};

export const cancelInvitation = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const token = await getToken();
      const response = await axios.delete(`${import.meta.env.VITE_API_URL}/app/invitations/${invitationCode}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return invitationSchema.parse(response.data);
    },
    onMutate: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["family"] });
    },
  });
};
